<template>
  <v-dialog
    :value="dialog"
    :max-width="400"
    persistent
    :retain-focus="false"
    scrollable
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
  >
    <v-card outlined>
      <v-card-title>
        {{ $t('contract.uploadContract') }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="py-5">
        <v-form ref="form" @submit.stop.prevent>
          <v-text-field
            type="text"
            v-model.trim="contract.title"
            :label="$t('contract.upload.title')"
            :rules="rules.title"
            prepend-icon="mdi-card-text-outline"
            tabindex="1"
            clearable
          ></v-text-field>

          <contract-signers-select
            v-model="contract.signers"
            :items="contactsList"
            :label="$t('contract.upload.signers')"
            prepend-icon="mdi-account-circle-outline"
            tabindex="1"
          ></contract-signers-select>

          <v-select
            v-model="contract.providerId"
            :items="signingProviders"
            :label="$t('contract.upload.provider')"
            :return-object="false"
            item-value="id"
            item-text="name"
            prepend-icon="mdi-clipboard-account-outline"
            tabindex="1"
            clearable
          ></v-select>

          <p-file-input
            v-model="file"
            :library-file-type="fileType.contract"
            :label="$t('contract.upload.file')"
            :rules="rules.file"
            hide-details="auto"
            tabindex="1"
            accept=".pdf"
            clearable
          ></p-file-input>

          <div class="text-center my-10">
            <v-btn
              color="primary"
              class="p-btn-rounded"
              tabindex="2"
              large
              rounded
              dark
              @click="submit"
            >
              {{ $t('contract.uploadContract') }}
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { fileType } from '@/constants'

import { mapActions, mapGetters } from 'vuex'

import ContractSignersSelect from './ContractSignersSelect'
import PBtnIcon from '@/components/PBtnIcon'
import PFileInput from '@/components/PFileInput'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


export default {
  components: {
    ContractSignersSelect,
    PBtnIcon,
    PFileInput
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  data () {
    return {
      contract: {},
      fileType,
      file: []
    }
  },


  computed: {
    ...mapGetters('signingProvider', ['signingProviders']),
    ...mapGetters('contact', ['contactById', 'shortContactById', 'shortContactsForProject']),
    ...mapGetters('event', ['eventDetailsById', 'eventsByProject']),
    ...mapGetters('member', ['membersByProject', 'memberById']),
    ...mapGetters('project', ['shortProjectById', 'currentProjectId']),

    contactsList () {
      return this.shortContactsForProject(this.currentProjectId)
    },

    rulesToApply () {
      const rule = this.rule

      return {
        title: [rule.required()],
        file: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('contract', ['createContract', 'uploadContractFile']),
    ...mapActions('contact', ['loadContactById']),
    ...mapActions('event', ['loadEventDetailsById']),
    ...mapActions('project', ['loadProjectById']),

    onDialogOpen () {
      this.initData()
      this.initWatchedModel('contract')
      this.resetValidation()
    },

    async updateContract () {
      this.fullContract = this.getObjectCopy(this.contract)
      this.fullContract.title = this.contract.title
      this.fullContract.projectId = this.currentProjectId
      if (this.file?.length) {
        this.fullContract.fileId = await this.uploadContractFile(this.file[0])
      }
    },

    initData () {
      this.file = null

      this.contract = {
        title: null,
        providerId: null,
        signers: [],
        status: 0,
        fileId: null
      }

      if (this.signingProviders?.length) {
        this.contract.providerId = this.signingProviders[0].id
      }
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return
      try {
        this.showSnackbarProcessing()
        await this.updateContract()
        await this.createContract(this.fullContract)
        this.showSnackbarSuccess(this.$t('contract.contractCreated'))
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
